import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { RootState } from '../../../store/store';

interface BotCardsRowProps {
  children: React.ReactNode;
  /** Extra class on the row, e.g. `bot-cards-row--flight` for wider cards. */
  className?: string;
}

/**
 * One horizontal, swipeable row of option cards — the layout every bot option
 * list uses (search results, room options, two-one-way legs).
 *
 * The track scrolls natively (touch swipe, trackpad, shift+wheel) and snaps to
 * cards. Prev/next buttons are always visible (touch screens included), each
 * only while there is something to scroll to in its direction; they sit in
 * the row's 32px edge gutter. Each direct child is one card.
 *
 * RTL: browsers report scrollLeft as 0 → negative in an RTL scroller, so the
 * position is read as |scrollLeft| and "next" scrolls towards the inline end.
 */
const BotCardsRow: React.FC<BotCardsRowProps> = ({ children, className }) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [isRtl, setIsRtl] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const pos = Math.abs(el.scrollLeft);
    setCanPrev(pos > 1);
    setCanNext(pos < max - 1);
    setIsRtl(window.getComputedStyle(el).direction === 'rtl');
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    // Cards change size as their images load; the panel is resizable.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [update]);

  useEffect(update, [children, update]);

  const scrollByPage = (towardsEnd: boolean) => {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.85, 120);
    const sign = (towardsEnd ? 1 : -1) * (isRtl ? -1 : 1);
    el.scrollBy({ left: sign * step, behavior: 'smooth' });
  };

  return (
    <div className={`bot-cards-row${className ? ` ${className}` : ''}`}>
      {canPrev && (
        <button
          type="button"
          className="bot-cards-row__arrow bot-cards-row__arrow--prev"
          aria-label={t?.cardsPrev ?? 'Previous options'}
          onClick={() => scrollByPage(false)}
        >
          {isRtl ? <IoChevronForward /> : <IoChevronBack />}
        </button>
      )}
      <div className="bot-cards-row__track" ref={trackRef}>
        {children}
      </div>
      {canNext && (
        <button
          type="button"
          className="bot-cards-row__arrow bot-cards-row__arrow--next"
          aria-label={t?.cardsNext ?? 'More options'}
          onClick={() => scrollByPage(true)}
        >
          {isRtl ? <IoChevronBack /> : <IoChevronForward />}
        </button>
      )}
    </div>
  );
};

export default BotCardsRow;
